import { useEffect, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import ListRequestVacation from "./listRequestVacation";
import VacationRequestForm from "./vacationRequestForm";
import { getVacationOverview, type VacationOverview } from "@/lib/collections/vacations/lib/BalanceService";
import { getRequestsByUser } from "@/lib/collections/vacations/vacationsService";
import type { VacationRequest } from "@/lib/collections/vacations/types";
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from "@/components/ui/breadcrumb";
import { ChartNoAxesColumn } from "lucide-react";

const errorMessage = (e: unknown): string => {
    if (e instanceof Error && e.message === "PROFILE_NOT_FOUND")
        return "No se encontró el perfil del colaborador.";
    if (e instanceof Error && e.message === "MISSING_DATES")
        return "El perfil no tiene fecha de ingreso o de nacimiento. RRHH debe completarlas.";

    if (e instanceof Error && e.message === "NOT_ELIGIBLE")
        return "Los pasantes no tienen derecho a vacaciones. Podrán pedirlas cuando pasen a nómina.";

    return e instanceof Error
        ? `No se pudo cargar el saldo de vacaciones: ${e.message}`
        : "No se pudo cargar el saldo de vacaciones.";
};

export default function VacationsPanel({ userId }: { userId: string }) {
    const [overview, setOverview] = useState<VacationOverview | null>(null);
    const [requests, setRequests] = useState<VacationRequest[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [reloadKey, setReloadKey] = useState(0);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError(null);

        Promise.all([getVacationOverview(userId), getRequestsByUser(userId)])
            .then(([ov, reqs]) => {
                if (cancelled) return;
                setOverview(ov);
                setRequests(reqs);
            })
            .catch((e) => {
                console.error(e);
                if (!cancelled) setError(errorMessage(e));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });


        return () => {
            cancelled = true;
        };
    }, [userId, reloadKey]);

    const reload = () => setReloadKey((k) => k + 1);

    const balance = overview?.balance;
    const pendingDays = requests
        .filter((r) => r.status === "pending")
        .reduce((sum, r) => sum + r.daysDeducted, 0);
    const availableForNew = Math.max(0, (balance?.availableBalance ?? 0) - pendingDays);

    return (


        <><Breadcrumb>
            <BreadcrumbList>
                <BreadcrumbItem>
                    <ChartNoAxesColumn />
                    <BreadcrumbLink asChild>
                        <BreadcrumbPage>Vacaciones</BreadcrumbPage>
                    </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                </BreadcrumbItem>
            </BreadcrumbList>
        </Breadcrumb>
            <div className="space-y-4 pt-6">
                <Card>
                    <CardHeader>
                        <CardTitle>Vacaciones</CardTitle>
                        {overview?.fullName && (
                            <p className="text-sm text-muted-foreground">{overview.fullName}</p>
                        )}
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {loading && <p className="text-sm text-muted-foreground">Cargando...</p>}
                        {error && <p className="text-sm text-destructive">{error}</p>}

                        {balance && (
                            <>
                                <div>
                                    <p className="text-4xl font-semibold">{balance.availableBalance}</p>
                                    <p className="text-sm text-muted-foreground">días disponibles</p>
                                </div>

                                <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                                    <div className="rounded-lg border p-3">
                                        <p className="text-lg font-medium">{balance.yearsOfService}</p>
                                        <p className="text-xs text-muted-foreground">años de antigüedad</p>
                                    </div>
                                    <div className="rounded-lg border p-3">
                                        <p className="text-lg font-medium">{balance.daysTaken}</p>
                                        <p className="text-xs text-muted-foreground">días aprobados</p>
                                    </div>
                                    <div className="rounded-lg border p-3">
                                        <p className="text-lg font-medium">{balance.accruedInCurrentPeriod}</p>
                                        <p className="text-xs text-muted-foreground">
                                            acumulados este año (aún no disponibles)
                                        </p>
                                    </div>
                                </div>

                                {balance.yearsOfService === 0 && (
                                    <Alert>
                                        <AlertDescription>
                                            Aún no cumple el primer año. Los días se acumulan, pero podrá tomarlos
                                            al cumplirlo.
                                        </AlertDescription>
                                    </Alert>
                                )}

                                {balance.hrAlert && (
                                    <Alert variant="destructive">
                                        <AlertDescription>
                                            Tiene acumulados {balance.accumulatedPeriods} períodos de vacaciones.
                                            Conviene que los tome pronto.
                                        </AlertDescription>
                                    </Alert>
                                )}
                            </>
                        )}
                    </CardContent>
                </Card>

                {overview && (
                    <VacationRequestForm
                        userId={userId}
                        canton={overview.canton}
                        availableDays={availableForNew}
                        onCreated={reload} />
                )}

                {overview && <ListRequestVacation requests={requests} onChanged={reload} />}
            </div></>
    );
}